import { checkDocumentSize } from '../core/limits';
import { createFillLayer } from '../core/model/document';
import { isGroup } from '../core/model/tree';
import type { GroupLayer, ImageLayer, Layer, TextLayer } from '../core/model/types';
import { textBox } from '../core/paint/text';
import type { LayerPlan, TransformPlan } from '../core/transforms';
import { applyPixelOp } from '../io/raster';
import { textLayerToDataUrl } from '../io/textRaster';
import type { AppContext } from './context';
import { beginDocumentEdit, STALE_EDIT_MESSAGE } from './staleGuard';

/**
 * Executes a `TransformPlan` as exactly one history entry.
 *
 * The plan says what to do; this does it — all pixel work first, then a single
 * `commit` — so undo restores the pre-transform document in one step and never
 * a half-transformed one.
 *
 * Scale policy per op, matching what the plans assume: crop and resample bake
 * the layer's scale into their output (scale resets to 1); rotate and flip do
 * not touch the raster's size (scale carries through). Text has no pixels to
 * bake, so its angle and scale changes ride on the plan's field replacements —
 * except under a flip, where mirrored glyphs are impossible for live text and
 * the layer is rasterized, announced, and left to undo to bring back.
 */
export async function runTransform(ctx: AppContext, plan: TransformPlan | null): Promise<void> {
  if (!plan) {
    ctx.notify('That transform is not possible on this document.', 'warn');
    return;
  }

  const doc = ctx.store.getState().doc;
  const size = checkDocumentSize(plan.width, plan.height);
  if (!size.ok) {
    ctx.notify(size.reason ?? 'That size is not supported.', 'warn');
    return;
  }

  try {
    const layers: Layer[] = [];
    let rasterizedText = false;
    // The commit below rebuilds a captured `doc`; a commit that landed inside
    // any of the awaits (text rasterization, per-layer pixel ops) would be
    // silently reverted by it.
    const documentStale = beginDocumentEdit(ctx);
    const result = await applyPlanToLayers(doc.layers, plan);
    layers.push(...result.layers);
    rasterizedText = result.rasterizedText;

    // A crop can exclude every layer; never leave a document without one.
    if (layers.length === 0) layers.push(createFillLayer('#ffffff', { name: 'Background' }));

    if (documentStale()) {
      ctx.notify(STALE_EDIT_MESSAGE, 'warn');
      return;
    }

    ctx.store.getState().commit(plan.label, {
      ...doc,
      width: plan.width,
      height: plan.height,
      layers,
    });
    ctx.store.getState().setSelection(null);
    if (rasterizedText) ctx.notify('Text layers were rasterized by the flip', 'warn');
    ctx.notify(`${plan.label} — ${plan.width} × ${plan.height}`);
  } catch (error) {
    ctx.notify(
      error instanceof Error ? error.message : 'That transform could not be applied.',
      'error',
    );
  }
}

/**
 * Apply a plan to a list of layers, groups included.
 *
 * The plan speaks about leaves — the layers that have pixels — so a group is
 * walked into rather than transformed. A group whose children all fall outside
 * the new canvas goes with them: an empty folder is not something the user
 * asked for.
 */
async function applyPlanToLayers(
  layers: Layer[],
  plan: TransformPlan,
): Promise<{ layers: Layer[]; rasterizedText: boolean }> {
  const out: Layer[] = [];
  let rasterizedText = false;

  for (const layer of layers) {
    if (isGroup(layer)) {
      const children = await applyPlanToLayers(layer.children, plan);
      rasterizedText = rasterizedText || children.rasterizedText;
      if (children.layers.length === 0) continue;
      out.push({ ...layer, children: children.layers } satisfies GroupLayer);
      continue;
    }

    const entry = plan.layers.find((candidate) => candidate.id === layer.id);
    if (!entry || entry.remove) continue;

    if (layer.type === 'text' && entry.op?.kind === 'flip') {
      out.push(await rasterizedTextLayer(layer, entry));
      rasterizedText = true;
      continue;
    }

    // Position first, plus the optional field replacements every layer type
    // shares: rotate 90 and flip carry an angle, resample carries a scale.
    let next: Layer = {
      ...layer,
      x: entry.x,
      y: entry.y,
      ...(entry.angle !== undefined ? { angle: entry.angle } : {}),
      ...(entry.scaleX !== undefined && entry.scaleY !== undefined
        ? { scaleX: entry.scaleX, scaleY: entry.scaleY }
        : {}),
    } as Layer;

    if (entry.op && (next.type === 'image' || next.type === 'paint')) {
      const source = next.type === 'image' ? next.src : next.pixels;
      const src = await applyPixelOp(source, entry.op);
      // The mask guards these pixels, so it goes through the identical
      // operation. Cropping the pixels but not the stencil would slide the
      // mask out from under the picture it hides.
      const mask = next.mask?.src
        ? { ...next.mask, src: await applyPixelOp(next.mask.src, entry.op) }
        : next.mask;
      const bakesScale = entry.op.kind === 'crop' || entry.op.kind === 'resample';
      const patch = {
        mask,
        ...(bakesScale ? { scaleX: 1, scaleY: 1 } : {}),
        ...(entry.naturalWidth !== undefined ? { naturalWidth: entry.naturalWidth } : {}),
        ...(entry.naturalHeight !== undefined ? { naturalHeight: entry.naturalHeight } : {}),
      };
      next = (
        next.type === 'image' ? { ...next, src, ...patch } : { ...next, pixels: src, ...patch }
      ) as Layer;
    }
    out.push(next);
  }

  return { layers: out, rasterizedText };
}

/**
 * The image layer a text layer becomes when a flip bakes it.
 *
 * Same identity, same place (the plan's mirrored geometry), same scale — the
 * glyphs are the only thing that changed, and they are pixels now.
 */
async function rasterizedTextLayer(layer: TextLayer, entry: LayerPlan): Promise<ImageLayer> {
  const box = textBox(layer);
  const png = await applyPixelOp(
    textLayerToDataUrl(layer),
    entry.op as NonNullable<LayerPlan['op']>,
  );
  return {
    id: layer.id,
    name: layer.name,
    type: 'image',
    visible: layer.visible,
    locked: layer.locked,
    opacity: layer.opacity,
    blendMode: layer.blendMode,
    x: entry.x,
    y: entry.y,
    scaleX: layer.scaleX,
    scaleY: layer.scaleY,
    angle: entry.angle ?? layer.angle,
    clipping: layer.clipping,
    mask: null,
    src: png,
    naturalWidth: Math.max(1, Math.round(box.width)),
    naturalHeight: Math.max(1, Math.round(box.height)),
  };
}
